import { put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from 'redux-starter-kit';
import {
  ListParams,
  Mutations,
  Requests,
  Resources,
  ResourceRequest,
  PositionTemplateFieldType,
} from '@erecruit/types';
import { getResourceApi } from '@erecruit/modules';

import {
  requestPositionTemplateDetailsFail,
  requestPositionTemplateDetailsSuccess,
  requestPositionTemplatesFail,
  requestPositionTemplatesSuccess,
  savePositionFail,
  savePositionSuccess,
  fetchCompaniesSuccess,
  setSelectedCompany,
} from './reducer';
import { PositionField } from '../../AddNewPositionWizard/types';

import {
  getDataSourceForField,
  getSelectFields,
  transformPositionTemplate,
} from './helpers';
import { generateFolderGroupId } from '../../AddNewPositionWizard/components/DynamicFormItems/helpers';
import { withStatusInfo } from '../../ActionStatus';
import { globalDispatch } from '../../../App/GlobalDispatchWatcher';

function* fetchData({
  payload,
}: PayloadAction<{ searchTerm: string; companyId: number }>) {
  try {
    const params: ListParams<Resources.PositionTemplateFilter> = {
      pager: {
        offset: 0,
        limit: 5,
      },
      filter: {
        searchTerm: payload.searchTerm,
        companyIds: [payload.companyId],
      },
    };

    if (payload.companyId === -1) {
      yield put(requestPositionTemplatesSuccess({ positionTemplates: [] }));
      return;
    }

    const responses = yield getResourceApi().getResources([
      new Requests.PositionTemplateRequest(params),
    ]);

    //@TODO add error checks
    const positionTemplates = responses[0].data as Resources.PositionTemplate[];

    yield put(requestPositionTemplatesSuccess({ positionTemplates }));
  } catch (e) {
    console.error(e);
    yield put(requestPositionTemplatesFail(e.message));
  }
}

function* fetchCompanies() {
  const response = yield getResourceApi().getResources([
    new Requests.CompanyRequest({}),
  ]);

  const companies: Resources.Company[] = response[0].data;

  if (companies.length === 1) {
    yield put(setSelectedCompany(companies[0].id));
  }

  yield put(fetchCompaniesSuccess(companies));
}

function* fetchPositionTemplateDetails({
  payload: selectedPositionTemplate,
}: PayloadAction<Resources.PositionTemplate>) {
  try {
    const params: Resources.PositionTemplateDetailsParams = {
      id: selectedPositionTemplate.id,
    };

    let responses = yield getResourceApi().getResources([
      new Requests.PositionTemplateDetailsRequest(params),
    ]);

    const positionTemplateDetails = responses[0]
      .data[0] as Resources.PositionTemplateDetails;

    const positionTemplate = transformPositionTemplate(positionTemplateDetails);

    const fieldsWithData: PositionField[] = [...positionTemplate.fields];
    const selectFields = getSelectFields([...positionTemplate.fields]);
    const resources: ResourceRequest[] = [];
    selectFields.forEach(field => {
      const dataSource = getDataSourceForField(field);
      if (dataSource) {
        resources.push(dataSource);
      }
    });

    responses = yield getResourceApi().getResources(resources);

    selectFields.forEach((field, index) => {
      const data = responses[index].data;
      const templateFieldIndex = positionTemplate.fields.findIndex(
        f => f.fieldType === field.fieldType,
      );

      if (templateFieldIndex !== -1) {
        const field = {
          ...positionTemplate.fields[templateFieldIndex],
        };

        field.data = data;

        if (field.fieldType === PositionTemplateFieldType.FolderGroup) {
          const folderGroupData = (field.data as unknown) as Resources.FolderGroup[];

          const value = field.value?.map(
            (value: { folderGroupId: number; folderGroupAliasId: number }) =>
              generateFolderGroupId({
                id: value.folderGroupId,
                aliasId: value.folderGroupAliasId,
              }),
          );

          // IN CONTRACT FOLDER GROUP IS MULTI SELECT BUT ON FRONTEND IT SHOULD BE SINGLE SELECT
          field.value = value[0];

          field.data = folderGroupData.map(folderGroup => {
            const folderGroupId = generateFolderGroupId({
              id: folderGroup.key.id,
              aliasId: folderGroup.key.aliasId,
            });

            return {
              id: folderGroupId,
              name: folderGroup.name,
            };
          });
        }

        fieldsWithData[templateFieldIndex] = field;
      }
    });

    const populatedPositionTemplate = {
      ...positionTemplate,
      fields: fieldsWithData,
    };

    yield put(requestPositionTemplateDetailsSuccess(populatedPositionTemplate));
  } catch (e) {
    console.error(e);
    yield put(requestPositionTemplateDetailsFail(e.message));
  }
}

function* savePosition({
  payload,
}: PayloadAction<Mutations.NewPositionParams>) {
  const mutationResponse = yield getResourceApi().createMutationRequest(
    new Requests.NewPositionRequest(payload),
  );
  yield put(savePositionSuccess(mutationResponse));
}

function* savePositionErrorHandler(e: Error) {
  yield put(savePositionFail(e.message));
}

function* watchForRequestData() {
  yield takeLatest('requestPosition/requestPositionTemplates', fetchData);
  yield takeLatest(
    'requestPosition/requestPositionTemplateDetails',
    fetchPositionTemplateDetails,
  );
  yield takeLatest('requestPosition/fetchCompanies', fetchCompanies);
  yield takeLatest(
    'requestPosition/savePosition',
    withStatusInfo({
      successGeneratorFn: savePosition,
      errorGeneratorFn: savePositionErrorHandler,
      autocloseMs: 1000,
      getSuccessMessage: ({ payload }) => ({
        id: messageIds.vms.newPositionWizard.requestPosition.successMessage,
        values: { position: payload.positionTitle || '' },
      }),
      getLoadingMessage: ({ payload }) => ({
        id: messageIds.vms.newPositionWizard.requestPosition.savingMessage,
        values: { position: payload.positionTitle || '' },
      }),
      getHeader: ({ payload }) => ({
        title: {
          id: messageIds.vms.newPositionWizard.requestPosition.savingTitle,
          values: { position: payload.positionTitle || '' },
        },
      }),
      getCustomErrorAction: ({ payload }) => ({
        display: { text: { id: messageIds.vms.errors.retry } },
        action: () =>
          globalDispatch({ type: 'requestPosition/savePosition', payload }),
      }),
    }),
  );
}

export default watchForRequestData;
